import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { message } from "antd"
import DisputeService from "services/DisputeService"

export const fetchPartnerDisputes = createAsyncThunk(
  "disputes/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await DisputeService.getAll(params)
      return response
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.error || "Erreur lors du chargement"
      )
    }
  }
)

export const fetchPartnerDisputeById = createAsyncThunk(
  "disputes/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await DisputeService.getById(id)
      return response
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.error || "Erreur lors du chargement"
      )
    }
  }
)

export const ignoreDispute = createAsyncThunk(
  "disputes/ignore",
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await DisputeService.ignore(id, reason)
      return response
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.error || "Erreur lors de l'action"
      )
    }
  }
)

export const acceptDispute = createAsyncThunk(
  "disputes/accept",
  async ({ id, message: msg }, { rejectWithValue }) => {
    try {
      const response = await DisputeService.accept(id, msg)
      return response
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.error || "Erreur lors de l'action"
      )
    }
  }
)

export const executeQuickAction = createAsyncThunk(
  "disputes/quickAction",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await DisputeService.quickAction(id, data)
      message.success("Action exécutée avec succès !")
      return response
    } catch (error) {
      const payload = error?.response?.data || {
        error: "Erreur lors de l'action",
      }
      message.error(payload.error || "Erreur lors de l'action")
      return rejectWithValue(payload)
    }
  }
)

export const fetchDisputeHistory = createAsyncThunk(
  "disputes/fetchHistory",
  async (id, { rejectWithValue }) => {
    try {
      const response = await DisputeService.getHistory(id)
      return response
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.error || "Erreur lors du chargement"
      )
    }
  }
)

const initialState = {
  disputes: [],
  loading: false,
  error: null,
  selectedDispute: null,
  history: [],
  detailLoading: false,
  actionLoading: false,
  actionError: null,
  lastActionMessage: null,
  meta: {
    page: 1,
    pageSize: 10,
    total: 0,
    pageCount: 1,
  },
}

const disputeSlice = createSlice({
  name: "disputes",
  initialState,
  reducers: {
    clearSelectedDispute: (state) => {
      state.selectedDispute = null
      state.history = []
      state.detailLoading = false
      state.error = null
    },
    resetActionState: (state) => {
      state.actionLoading = false
      state.actionError = null
      state.lastActionMessage = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPartnerDisputes.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPartnerDisputes.fulfilled, (state, action) => {
        state.loading = false
        state.disputes = action.payload?.data || []
        if (action.payload?.meta) {
          state.meta = {
            page: action.payload.meta.page ?? state.meta.page,
            pageSize: action.payload.meta.pageSize ?? state.meta.pageSize,
            total: action.payload.meta.total ?? state.meta.total,
            pageCount:
              action.payload.meta.pageCount ?? state.meta.pageCount,
          }
        }
      })
      .addCase(fetchPartnerDisputes.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    builder
      .addCase(fetchPartnerDisputeById.pending, (state) => {
        state.detailLoading = true
        state.error = null
      })
      .addCase(fetchPartnerDisputeById.fulfilled, (state, action) => {
        state.detailLoading = false
        state.selectedDispute = action.payload?.data || null
      })
      .addCase(fetchPartnerDisputeById.rejected, (state, action) => {
        state.detailLoading = false
        state.error = action.payload
      })

    builder
      .addCase(ignoreDispute.pending, (state) => {
        state.actionLoading = true
        state.actionError = null
        state.lastActionMessage = null
      })
      .addCase(ignoreDispute.fulfilled, (state, action) => {
        state.actionLoading = false
        const updated = action.payload?.data
        if (updated) {
          state.disputes = state.disputes.map((item) =>
            item.id === updated.id ? updated : item
          )
          if (state.selectedDispute?.id === updated.id) {
            state.selectedDispute = updated
          }
        }
        state.lastActionMessage = action.payload?.message || null
      })
      .addCase(ignoreDispute.rejected, (state, action) => {
        state.actionLoading = false
        state.actionError = action.payload
      })

    builder
      .addCase(acceptDispute.pending, (state) => {
        state.actionLoading = true
        state.actionError = null
        state.lastActionMessage = null
      })
      .addCase(acceptDispute.fulfilled, (state, action) => {
        state.actionLoading = false
        const updated = action.payload?.data
        if (updated) {
          state.disputes = state.disputes.map((item) =>
            item.id === updated.id ? updated : item
          )
          if (state.selectedDispute?.id === updated.id) {
            state.selectedDispute = updated
          }
        }
        state.lastActionMessage = action.payload?.message || null
      })
      .addCase(acceptDispute.rejected, (state, action) => {
        state.actionLoading = false
        state.actionError = action.payload
      })

    // Quick action
    builder
      .addCase(executeQuickAction.pending, (state) => {
        state.actionLoading = true
        state.actionError = null
      })
      .addCase(executeQuickAction.fulfilled, (state, action) => {
        state.actionLoading = false
        const updated = action.payload?.data
        if (updated) {
          state.disputes = state.disputes.map((item) =>
            item.id === updated.id ? updated : item
          )
          if (state.selectedDispute?.id === updated.id) {
            state.selectedDispute = updated
          }
        }
      })
      .addCase(executeQuickAction.rejected, (state, action) => {
        state.actionLoading = false
        state.actionError = action.payload
      })

    // Fetch history
    builder
      .addCase(fetchDisputeHistory.fulfilled, (state, action) => {
        state.history = action.payload?.data || []
      })
  },
})

export const { clearSelectedDispute, resetActionState } = disputeSlice.actions
export default disputeSlice.reducer
